import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import Accordion from '@Pages/ProfileSettings/Preferences/components/Accordion';
import { ButtonWithIcon } from '@els/els-react--button';

import './ProfileSettingsAccordionItem.scss';

function ProfileSettingsAccordionItem({
  onClose,
  children,
  onSave,
  label,
  value,
  title,
  className,
  notTagged,
  resetButton,
  onReset = () => {},
  formValid = true,
  validateOnSave = false,
}) {
  const [open, setOpen] = useState(false);

  const toggleOpen = () => {
    setOpen(!open);
    onClose();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleReset = (e) => {
    e.stopPropagation();
    onReset(null, () => setOpen(false));
  };

  const handleSave = () => {
    if (!validateOnSave) {
      onSave();
      setOpen(false);
      return;
    }
    onSave(() => setOpen(false));
  };

  const renderValue = () => {
    if (isArray(value)) {
      return (
        <span className="profile-settings-accordion-row_list">
          {/* eslint-disable-next-line no-shadow */}
          {value.map(({ title, value }, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`${title}-${value}_${index}`}
              className={classnames({
                'profile-settings-accordion-row_list-tagged': !notTagged && value,
              })}>
              {value}
            </span>
          ))}
        </span>
      );
    }

    return (
      <span
        className={classnames({
          'profile-settings-accordion-row_value-tagged': !notTagged && value,
          'profile-settings-accordion-row_value-titled': !value && title,
        })}>
        {value || title}
      </span>
    );
  };

  return (
    <Accordion
      open={open}
      className={className}
      heading={
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          data-e2e="profile-settings-accordion-row"
          className="rx-accordion-row profile-settings-accordion-row"
          onClick={toggleOpen}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex="0"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleOpen();
            }
          }}>
          {resetButton && (
            <div className="profile-settings-accordion-row_reset">
              <ButtonWithIcon
                type="link"
                size="small"
                linkIconSize="s"
                data-e2e="profile-settings-accordion-row-reset"
                sprite="Undo"
                iconRight
                onClick={handleReset}
                className="e2e-profile-settings-reset">
                Reset to default
              </ButtonWithIcon>
            </div>
          )}
          <div className="profile-settings-accordion-row_icon">
            {!open && (
              <ButtonWithIcon
                type="link"
                size="small"
                linkIconSize="s"
                data-e2e="profile-settings-accordion-row-edit"
                sprite="Edit"
                iconOnly
                onClick={() => {}}>
                Edit
              </ButtonWithIcon>
            )}
            {open && (
              <ButtonWithIcon
                type="link"
                size="small"
                linkIconSize="s"
                sprite="Close"
                data-e2e="profile-settings-accordion-row-close}"
                iconOnly
                onClick={handleClose}>
                Close
              </ButtonWithIcon>
            )}
          </div>

          <span className="profile-settings-accordion-row_label">{label}</span>
          {!open && renderValue()}
        </div>
      }
      body={
        <div className="profile-settings-accordion-row__content">
          <form noValidate>
            {open ? children : null}
            {open ? (
              <div className="profile-settings-accordion-row__content_button u-els-margin-top-2x">
                <ButtonWithIcon
                  type="primary"
                  size="small"
                  sprite="ChevronRight"
                  linkIconSize="s"
                  data-e2e="profile-settings-accordion-row-save"
                  iconRight
                  disabled={!formValid}
                  onClick={handleSave}
                  className="e2e-profile-settings-save">
                  Save
                </ButtonWithIcon>
              </div>
            ) : null}
          </form>
        </div>
      }
    />
  );
}

ProfileSettingsAccordionItem.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  onSave: PropTypes.func.isRequired,
  formValid: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.any,
  title: PropTypes.string,
  validateOnSave: PropTypes.bool,
  className: PropTypes.string,
  notTagged: PropTypes.bool,
  resetButton: PropTypes.bool,
  onReset: PropTypes.func,
};

export default ProfileSettingsAccordionItem;
